import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Dispute {
  id: string;
  order_id: string;
  user_id: string;
  reason: string;
  description: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'rejected';
  admin_response: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  order?: {
    order_number: string;
    product?: {
      title: string;
    };
  };
  user?: {
    full_name: string;
    email?: string;
  };
}

export const AdminDisputesManager = () => {
  const { toast } = useToast();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [newStatus, setNewStatus] = useState<string>('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchDisputes();

    const channel = supabase
      .channel('admin-disputes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_disputes'
        },
        () => {
          fetchDisputes();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchDisputes = async () => {
    try {
      const { data, error } = await supabase
        .from('order_disputes')
        .select(`
          *,
          order:orders(
            order_number,
            product:products(title)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch user profiles separately
      const disputesWithUsers = await Promise.all(
        (data || []).map(async (dispute) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', dispute.user_id)
            .single();
          
          return {
            ...dispute,
            user: profile
          };
        })
      );
      
      setDisputes(disputesWithUsers as any);
    } catch (error) {
      console.error('Erreur lors du chargement des réclamations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDispute = (dispute: Dispute) => {
    setSelectedDispute(dispute);
    setAdminResponse(dispute.admin_response || '');
    setNewStatus(dispute.status);
    setDialogOpen(true);
  };

  const handleUpdateDispute = async () => {
    if (!selectedDispute) return;

    setUpdating(true);
    try {
      const updateData: any = {
        status: newStatus,
        admin_response: adminResponse.trim() || null,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'resolved' || newStatus === 'rejected') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('order_disputes')
        .update(updateData)
        .eq('id', selectedDispute.id);

      if (error) throw error;

      // Insert in-app notification for the buyer
      try {
        const statusLabels: Record<string, string> = {
          resolved: '✅ Réclamation résolue',
          rejected: '❌ Réclamation rejetée',
          in_progress: '🔄 Réclamation en cours de traitement',
        };
        const title = statusLabels[newStatus] || '📋 Mise à jour de réclamation';
        const message = adminResponse.trim()
          ? `Réponse de l'administration : ${adminResponse.trim()}`
          : `Le statut de votre réclamation a été mis à jour.`;
        await supabase.from('notifications').insert({
          user_id: selectedDispute.user_id,
          type: 'dispute_update',
          title,
          message,
          data: { dispute_id: selectedDispute.id, order_id: selectedDispute.order_id, status: newStatus },
        });
      } catch (notifError) {
        console.warn('Failed to insert dispute notification (non-blocking):', notifError);
      }

      toast({ title: 'Réclamation mise à jour avec succès' });
      setDialogOpen(false);
      fetchDisputes();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = {
      pending: { 
        label: 'En attente', 
        variant: 'secondary' as const, 
        icon: Clock 
      },
      in_progress: { 
        label: 'En cours', 
        variant: 'default' as const, 
        icon: AlertTriangle 
      },
      resolved: { 
        label: 'Résolue', 
        variant: 'default' as const, 
        icon: CheckCircle 
      },
      rejected: { 
        label: 'Rejetée', 
        variant: 'destructive' as const, 
        icon: XCircle 
      }
    };

    const { label, variant, icon: Icon } = config[status as keyof typeof config] || config.pending;
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const getReasonLabel = (reason: string) => {
    const reasons: Record<string, string> = {
      defective: 'Produit défectueux',
      wrong_item: 'Mauvais article',
      damaged: 'Endommagé',
      missing_parts: 'Pièces manquantes',
      not_as_described: 'Non conforme à la description',
      quality_issue: 'Problème de qualité',
      other: 'Autre'
    };
    return reasons[reason] || reason;
  };

  const pendingCount = disputes.filter(d => d.status === 'pending').length;
  const inProgressCount = disputes.filter(d => d.status === 'in_progress').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Réclamations clients</h2>
          <p className="text-muted-foreground">
            Gérez les réclamations et litiges des acheteurs
          </p>
        </div>
        <div className="flex gap-2">
          {pendingCount > 0 && (
            <Badge variant="destructive">{pendingCount} en attente</Badge>
          )}
          {inProgressCount > 0 && (
            <Badge variant="default">{inProgressCount} en cours</Badge>
          )}
        </div>
      </div>

      <div className="grid gap-4">
        {loading ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Chargement...
            </CardContent>
          </Card>
        ) : disputes.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Aucune réclamation
            </CardContent>
          </Card>
        ) : (
          disputes.map((dispute) => (
            <Card 
              key={dispute.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleOpenDispute(dispute)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">
                        Commande #{dispute.order?.order_number}
                      </CardTitle>
                      {getStatusBadge(dispute.status)}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>
                        <span className="font-semibold">Client: </span>
                        {dispute.user?.full_name || 'Utilisateur'}
                      </p>
                      <p>
                        <span className="font-semibold">Produit: </span>
                        {dispute.order?.product?.title || 'N/A'}
                      </p>
                      <p>
                        <span className="font-semibold">Motif: </span>
                        {getReasonLabel(dispute.reason)}
                      </p>
                      <p className="text-xs">
                        {new Date(dispute.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm line-clamp-2">{dispute.description}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Réclamation - Commande #{selectedDispute?.order?.order_number}
            </DialogTitle>
          </DialogHeader>
          {selectedDispute && (
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="font-semibold">Client: </span>
                  {selectedDispute.user?.full_name || 'Utilisateur'}
                </div>
                <div className="text-sm">
                  <span className="font-semibold">Produit: </span>
                  {selectedDispute.order?.product?.title || 'N/A'}
                </div>
                <div className="text-sm">
                  <span className="font-semibold">Motif: </span>
                  {getReasonLabel(selectedDispute.reason)}
                </div>
                <div className="text-sm">
                  <span className="font-semibold">Statut: </span>
                  {getStatusBadge(selectedDispute.status)}
                </div>
                <div className="text-sm text-muted-foreground">
                  Créée le {new Date(selectedDispute.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Description de la réclamation</h4>
                <p className="text-sm whitespace-pre-wrap">{selectedDispute.description}</p>
              </div>

              {selectedDispute.admin_response && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Réponse précédente</h4>
                  <p className="text-sm whitespace-pre-wrap bg-muted p-3 rounded">
                    {selectedDispute.admin_response}
                  </p>
                </div>
              )}

              <div className="border-t pt-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Statut</label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="in_progress">En cours</SelectItem>
                      <SelectItem value="resolved">Résolue</SelectItem>
                      <SelectItem value="rejected">Rejetée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold">Réponse de l'administrateur</label>
                  <Textarea
                    placeholder="Votre réponse au client..."
                    value={adminResponse}
                    onChange={(e) => setAdminResponse(e.target.value)}
                    rows={4}
                  />
                </div>

                <Button 
                  onClick={handleUpdateDispute}
                  disabled={updating}
                  className="w-full"
                >
                  {updating ? 'Mise à jour...' : 'Mettre à jour la réclamation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
